import React, { useState, useEffect, useCallback } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { createSelector } from 'reselect';
import PropTypes from 'prop-types';
import { useAsync } from 'react-async-hook';
import { Col, Form, Grid, Row } from 'react-bootstrap';
import Toggle from 'react-bootstrap-toggle';
import { Map } from 'immutable';
import {
  cloneDeep,
  filter,
  find,
  flow,
  get,
  getOr,
  isEmpty,
  merge,
  set,
  sum,
} from 'lodash/fp';
import { toFixed } from 'accounting';
import {
  CLIENT_TYPE,
  Condition,
  CreditRating,
  FeeCategory,
  FinanceType,
  FixedValueFee,
  MileageType,
  Origin,
} from '@upstart/harmony/lib/constants';
import { getAprConfigForCar } from '@upstart/harmony/lib/car-utils';
import {
  getDealer,
  getCar,
  getDeal,
  getPencil,
  getPayments,
  getError,
  getFetching,
} from '../../redux/deals/selectors';
import SidebarContainer from '../SidebarContainer';
import DealerNav from '../../components/DealerNav';
import * as carActions from '../../redux/cars/actions';
import * as dealActions from '../../redux/deals/actions';
import FinanceTypeDropdown from './FinanceTypeDropdown';
import LookupVehicle from './LookupVehicle';
import SummaryRow from './SummaryRow';
import SummaryBreakdownRow from './SummaryBreakdownRow';
import DealerPriceRulesService from '../../services/DealerPriceRulesService';
import FeeRow from './FeeRow';
import AddFeeSection from './AddFeeSection';
import './Worksheet.scss';

function CarDetails(props) {
  const { dealer, car, deal, pencil } = props;
  return (
    <div>
      <div>Dealer: {dealer && dealer.name}</div>
      {car && (
        <>
          <div>VIN: {car.id}</div>
          <div>Stock: {car.stock}</div>
          <div>Name: {car.name}</div>
          <div>Deal Id: {deal && deal.id}</div>
          <div>Pencil Id: {pencil && pencil.id}</div>
        </>
      )}
    </div>
  );
}

CarDetails.propTypes = {
  dealer: PropTypes.object,
  car: PropTypes.object,
  deal: PropTypes.object,
  pencil: PropTypes.object,
};

function Worksheet(props) {
  const {
    actions,
    dealer,
    car,
    deal,
    payments,
    pencil,
    match,
    error,
    fetching,
  } = props;

  // Initialize data
  const initialFormData = Map({
    msrp: 0,
    mrm: 0,
    price: 0,
    residualValue: 0,
    residualPercentage: 0,
    rate: 0,
    term: 0,
    downPayment: 0,
    dealerCash: 0,
    netTrade: 0,
    rebate: 0,
    programs: null,
    fees: null,
    taxBreakdown: null,
  });
  const [formData, setFormData] = useState(initialFormData);
  // @TODO: add all of these but lease is the most urgent now.
  const [financeType, setFinanceType] = useState(FinanceType.LEASE);
  // @TODO: check for new car because leases are not valid for used cars

  const fetchDealerPriceRules = useAsync(DealerPriceRulesService.get, [
    dealer?.id,
  ]);
  const dealerPriceRules = fetchDealerPriceRules.result;

  // Helper function for setting data within an object
  const setFormAttributes = useCallback(
    updates => {
      const toMerge = Map(updates);
      const merged = formData.merge(toMerge);
      setFormData(merged);
    },
    [formData],
  );

  // Listen to changes on car to change the form
  useEffect(() => {
    if (!car || fetching) {
      return;
    }
    if (!deal) {
      // Create a deal only if none exists
      actions.onlineDealCreateRequest({
        car_id: car.id,
        dealer_id: dealer.id,
        builder: {
          // @TODO: flip this if we are doing an in-store deal
          overrideClientType: CLIENT_TYPE.ONLINE,
        },
        origin: Origin.ONLINE,
      });
    } else {
      // Otherwise update the car on the deal
      actions.dealUpdateRequest({
        ...deal,
        car_id: car.id,
      });
    }
    // If there is a pencil that exists we now have to clear that pencil as it
    // is invalid for the new car.
    if (pencil) {
      actions.pencilCreateRequest({
        pencil: {
          dealId: deal.id,
          carId: car.id,
        },
      });
    }
    // Reset attributes on car
    setFormAttributes({
      msrp: car.msrp,
      mrm: car.mrm,
      residualValue: 0,
      residualPercentage: 0,
    });
  }, [car]);

  useEffect(() => {
    // Payments are only relevant if you have a car.
    if (car && deal && !fetching) {
      if (!pencil) {
        actions.pencilCreateRequest({
          pencil: {
            dealId: deal.id,
            carId: car.id,
          },
        });
      }
      actions.paymentsRequest({
        dealId: deal.id,
        pencilId: pencil ? pencil.id : null,
        clientType: get('builder.overrideClientType')(deal),
        verbose: true,
      });
    }
  }, [deal]);

  // Refresh payments on a pencil change.
  useEffect(() => {
    if (car && deal && pencil) {
      actions.paymentsRequest({
        dealId: deal.id,
        pencilId: pencil ? pencil.id : null,
        clientType: get('builder.overrideClientType')(deal),
        verbose: true,
      });
    }
  }, [deal]);

  const offers = find({ financeType })(payments?.allOffers);
  const firstOffer = get('offers[0]')(offers);

  // Formulas for MRM calculation
  const shouldUseMRM = useCallback(
    (mrm, msrp) => {
      const ignoreMRM = get('desking.ignoreMRM')(dealer);
      // Some dealers like Jordan Ford ignore MRM
      if (ignoreMRM) {
        return false;
      }
      // Otherwise we use MRM
      return mrm && mrm < msrp;
    },
    [dealer],
  );
  const getAppliedMRM = useCallback(
    (mrm, msrp) => (shouldUseMRM(mrm, msrp) ? mrm : msrp),
    [shouldUseMRM],
  );

  useEffect(() => {
    if (firstOffer) {
      const appliedMRM = getAppliedMRM(car.mrm, car.msrp);

      // Only want to update this when we are getting a new car.
      const residualPercentage = formData.get('residualPercentage')
        ? formData.get('residualPercentage')
        : 100 * (firstOffer.residual / appliedMRM);

      setFormAttributes({
        price: firstOffer.price,
        residualValue: firstOffer.residual,
        residualPercentage,
        rate: firstOffer.rate,
        term: firstOffer.months,
        downPayment: firstOffer.downPayment,
        dealerCash: firstOffer.dealerCash,
        netTrade: firstOffer.netTrade,
        rebate: firstOffer.rebate,
        programs: firstOffer.programs,
        fees: firstOffer.fees,
        taxBreakdown: firstOffer.taxBreakdown,
      });
    }
  }, [payments]);

  // Load dealer from the URL
  useEffect(() => {
    actions.dealerRequest({ dealerId: match.params.dealerId });
  }, [actions, match.params.dealerId]);

  // Short circuit if we are missing the dealer
  if (!dealer || !dealer.id) {
    return null;
  }

  // Get apr config for the vehicle
  const aprConfig = car
    ? getAprConfigForCar(
        car,
        dealer?.aprConfig,
        dealerPriceRules,
        CLIENT_TYPE.ONLINE,
        Condition.NEW,
      )
    : null;
  // 150 basis points -> 0.00015 markup
  const rateMarkup =
    0.00001 *
      get(`lease.credit.${CreditRating.EXCELLENT}.moneyFactor.mileage1`)(
        aprConfig,
      ) || 0;
  const initialRate = formData.get('rate') - rateMarkup;

  // Get other info to display
  const carResidualValues = get(`residualValues.${formData.get('term')}`)(car);

  // Update the pencil if it exists. Otherwise this is a no-op.
  // @param {updates} object of updates to make.
  //
  // @TODO: right now this is hardcoded to the first offer.
  const updatePencilLeaseDataIfExists = updates => {
    if (pencil) {
      const clonePencil = cloneDeep(pencil);
      const toMerge = {
        data: {
          lease: [updates],
        },
      };
      const newPencil = merge(clonePencil, toMerge);
      actions.pencilUpdateRequest({ pencil: newPencil });
    }
  };

  // Editing MSRP will also change the residual value based on residual %
  const handleMSRPChange = value => {
    const msrp = parseFloat(value, 10);
    if (formData.get('residualPercentage')) {
      const appliedMRM = getAppliedMRM(formData.get('mrm'), msrp);
      const residualValue =
        0.01 * formData.get('residualPercentage') * appliedMRM;
      setFormAttributes({
        msrp,
        residualValue,
      });
      updatePencilLeaseDataIfExists({ residual: residualValue });
    } else {
      setFormAttributes({
        msrp,
      });
    }
  };

  const handlePriceChange = value => {
    const price = parseFloat(value, 10);
    setFormAttributes({
      price,
    });
    updatePencilLeaseDataIfExists({ price });
  };

  // We update both the deal and pencil.
  // In the future could refactor to just update the pencil.
  const handleDownPaymentChange = value => {
    const downPayment = parseFloat(value, 10);
    setFormAttributes({
      downPayment,
    });
    actions.dealUpdateRequest({ ...deal, downPayment });
    updatePencilLeaseDataIfExists({ downPayment });
  };

  const handleRateChange = value => {
    const rate = parseFloat(value, 10);
    setFormAttributes({
      rate,
    });
    updatePencilLeaseDataIfExists({ rate });
  };

  // Residual Value changes Percentage
  const handleResidualValueChange = value => {
    const residualValue = parseFloat(value, 10);
    const appliedMRM = getAppliedMRM(formData.get('mrm'), formData.get('msrp'));
    const residualPercentage = appliedMRM
      ? 100 * (residualValue / appliedMRM)
      : 0;
    setFormAttributes({
      residualPercentage,
      residualValue,
    });
    updatePencilLeaseDataIfExists({ residual: residualValue });
  };

  // Residual Percentage changes Value
  const handleResidualPercentageChange = value => {
    const residualPercentage = parseFloat(value, 10);
    const appliedMRM = getAppliedMRM(formData.get('mrm'), formData.get('msrp'));
    const residualValue = 0.01 * residualPercentage * appliedMRM;
    setFormAttributes({
      residualPercentage,
      residualValue,
    });
    updatePencilLeaseDataIfExists({ residual: residualValue });
  };

  const handleTermChange = value => {
    const term = parseInt(value, 10);
    setFormAttributes({
      term,
    });
    updatePencilLeaseDataIfExists({ months: term });
  };

  // Fee data is indexed differently.
  const updatePencilFeesIfExists = fees => {
    if (pencil) {
      const clonePencil = cloneDeep(pencil);
      const newPencil = flow(
        set(
          'data.fees.New.lease.capitalizedCost',
          filter({
            category: FeeCategory.CAPITALIZED_COST,
          })(fees),
        ),
        set(
          'data.fees.New.lease.capitalizedCostTaxable',
          filter({
            category: FeeCategory.CAPITALIZED_COST_TAXABLE,
          })(fees),
        ),
        set(
          'data.fees.New.lease.nontaxable',
          filter({
            category: FeeCategory.NON_TAXABLE,
          })(fees),
        ),
        set(
          'data.fees.New.lease.taxable',
          filter({
            category: FeeCategory.TAXABLE,
          })(fees),
        ),
      )(clonePencil);
      actions.pencilUpdateRequest({ pencil: newPencil });
    }
  };

  const handleDealerCashChange = value => {
    const dealerCash = parseInt(value, 10);
    setFormAttributes({ dealerCash });
    updatePencilLeaseDataIfExists({ dealerCash });
  };

  const handleNetTradeChange = value => {
    const netTrade = parseInt(value, 10);
    setFormAttributes({ netTrade });
    if (pencil) {
      const clonePencil = cloneDeep(pencil);
      const newPencil = set('data.tradeInAllowance', netTrade)(clonePencil);
      actions.pencilUpdateRequest({ pencil: newPencil });
    }
  };

  const handleRebateChange = value => {
    const rebate = parseInt(value, 10);
    setFormAttributes({ rebate });
    updatePencilLeaseDataIfExists({ rebate });
  };

  const handleFeeChange = i => value => {
    const fee = parseFloat(value, 10);
    const fees = cloneDeep(formData.get('fees'));
    fees[i].value = fee;
    setFormAttributes({
      fees,
    });
    updatePencilFeesIfExists(fees);
  };

  const handleFeeCategoryChange = i => category => {
    const fees = cloneDeep(formData.get('fees'));
    fees[i].category = category;
    setFormAttributes({
      fees,
    });
    updatePencilFeesIfExists(fees);
  };

  const handleFeeDelete = i => () => {
    const fees = cloneDeep(formData.get('fees'));
    fees.splice(i, 1);
    setFormAttributes({
      fees,
    });
    updatePencilFeesIfExists(fees);
  };

  const handleFeeAdd = (name, value, category) => {
    const fees = cloneDeep(formData.get('fees'));
    fees.push({
      type: FixedValueFee,
      name,
      value,
      category,
    });
    setFormAttributes({
      fees,
    });
    updatePencilFeesIfExists(fees);
  };

  const handleTaxChange = type => value => {
    const rate = 0.01 * parseFloat(value, 10);
    const taxBreakdown = set(
      `${type}.rate`,
      rate,
    )(formData.get('taxBreakdown'));
    setFormAttributes({
      taxBreakdown,
    });
    updatePencilLeaseDataIfExists({
      taxBreakdown,
      tax: sum(
        getOr(0, 'state.rate')(taxBreakdown),
        getOr(0, 'county.rate')(taxBreakdown),
        getOr(0, 'city.rate')(taxBreakdown),
      ),
    });
  };

  const getErrorIfExists = () => {
    if (error) {
      return 'Please add valid VIN';
    }
    if (car && dealer && car.dealer_id !== dealer.id) {
      return `Car belongs to another dealer: ${car.dealer_id}`;
    }
    return null;
  };

  // @TODO: add credit rating dropdown similar to finance type dropdown but refactored.
  return (
    <SidebarContainer title={dealer.name} logo={dealer.logo}>
      <div className="dealers-table desking-worksheet">
        <DealerNav />
        <div className="dealer-editor-container tabbed-page-container">
          <Grid fluid>
            <Row>
              <Col md={4}>
                <FinanceTypeDropdown
                  financeType={financeType}
                  setFinanceType={setFinanceType}
                  deal={deal}
                  updateDeal={actions.dealUpdateRequest}
                />
                <div className="dealOptions">
                  <div>Client: Online</div>
                  <div>
                    Condition (Only New cars can be leased):{' '}
                    {get('condition')(car)}
                  </div>
                  {/* @TODO: fix credit rating */}
                  <div>Credit Rating: Excellent</div>
                  <div>
                    Mileage:{' '}
                    {get(`lease.mileages.mileage1`)(aprConfig) ||
                      MileageType.LOW_MILEAGE}
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <LookupVehicle fetchCar={actions.carRequest} />
                <div className="error">{getErrorIfExists()}</div>
              </Col>
              <Col md={2}>
                <CarDetails
                  dealer={dealer}
                  car={car}
                  deal={deal}
                  pencil={pencil}
                />
              </Col>
            </Row>
            <hr />
            <Row>
              <Col md={4} className="leftSummary">
                <Form>
                  <SummaryRow
                    name="msrp"
                    label="MSRP"
                    value={formData.get('msrp')}
                    onChange={handleMSRPChange}
                  />
                  <SummaryRow
                    name="mrm"
                    label="MRM"
                    value={formData.get('mrm')}
                    readOnly
                  />
                  <hr />
                  <SummaryRow
                    name="price"
                    label="Selling Price"
                    value={formData.get('price')}
                    onChange={handlePriceChange}
                  />
                  <SummaryBreakdownRow
                    label="Display Price"
                    name="displayPrice"
                    offer={firstOffer}
                  />
                  <SummaryBreakdownRow
                    label={`Total Taxes (${dealer.state})`}
                    name="taxAmount"
                    offer={firstOffer}
                  />
                  <SummaryBreakdownRow
                    label="Cap Cost Reduction"
                    name="netCapCostReduction"
                    offer={firstOffer}
                  />
                  <SummaryBreakdownRow
                    label="Adjusted Cap Cost"
                    name="capitalizedCost"
                    offer={firstOffer}
                  />
                  <hr />
                  <SummaryRow
                    name="residualValue"
                    label={`Residual Value (using ${
                      shouldUseMRM(formData.get('mrm'), formData.get('msrp'))
                        ? 'MRM'
                        : 'MSRP'
                    })`}
                    math="-"
                    value={formData.get('residualValue')}
                    onChange={handleResidualValueChange}
                    precision={2}
                  />
                  <SummaryRow
                    name="leaseTerm"
                    label="Lease Term"
                    math="&#247;"
                    value={formData.get('term')}
                    readOnly
                  />
                  <SummaryBreakdownRow
                    label="Base Monthly Payment"
                    name="baseMonthlyPayment"
                    offer={firstOffer}
                  />
                  <SummaryBreakdownRow
                    label="Monthly Tax"
                    name="monthlyTax"
                    offer={firstOffer}
                  />
                  <SummaryBreakdownRow
                    label="Total Monthly Payment"
                    name="estimatedMonthlyPayment"
                    offer={firstOffer}
                  />
                  <hr />
                  <SummaryBreakdownRow
                    label="Total At Start"
                    name="totalAtStart"
                    offer={firstOffer}
                  />
                  <SummaryBreakdownRow
                    label="Inception Fees"
                    name="inceptionFees"
                    offer={firstOffer}
                  />
                </Form>
              </Col>
              <Col md={5} className="middleSummary">
                <Form>
                  <SummaryRow
                    name="downPayment"
                    label="Down Payment"
                    value={formData.get('downPayment')}
                    onChange={handleDownPaymentChange}
                  />
                  <SummaryRow
                    name="rate"
                    label="Rate"
                    value={formData.get('rate')}
                    onChange={handleRateChange}
                    precision={6}
                  />
                  <div className="breakdown">
                    {formData.get('rate') ? (
                      <div>
                        + Initial Rate: {toFixed(initialRate, 6)} (
                        {toFixed(initialRate * 2400, 2)}% APR)
                      </div>
                    ) : null}
                    {rateMarkup > 0 && (
                      <div>
                        + Markup: {toFixed(rateMarkup, 6)} (
                        {100000 * rateMarkup} basis points)
                      </div>
                    )}
                  </div>
                  <SummaryRow
                    name="term"
                    label="Term"
                    value={formData.get('term')}
                    onChange={handleTermChange}
                  />
                  <SummaryRow
                    name="residualPercentage"
                    label="Residual %"
                    value={formData.get('residualPercentage')}
                    onChange={handleResidualPercentageChange}
                    // This is to avoid rounding issues when changing residualValue
                    precision={1}
                  />
                </Form>
                <SummaryRow
                  name="dealerCash"
                  label="Dealer Cash"
                  value={formData.get('dealerCash')}
                  onChange={handleDealerCashChange}
                />
                <SummaryRow
                  name="netTrade"
                  label="Net Trade"
                  value={formData.get('netTrade')}
                  onChange={handleNetTradeChange}
                />
                <SummaryRow
                  name="rebate"
                  label="Rebate"
                  value={formData.get('rebate')}
                  onChange={handleRebateChange}
                />
                <div>
                  {formData.get('fees') && <div className="header">Fees:</div>}
                  <div className="indent">
                    {formData.get('fees') &&
                      formData.get('fees').map((fee, i) => (
                        <FeeRow
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${fee.name}-${fee.value}-${fee.category}-${i}`}
                          name={fee.name}
                          label={fee.name}
                          value={fee.value}
                          category={fee.category}
                          onValueChange={handleFeeChange(i)}
                          onCategoryChange={handleFeeCategoryChange(i)}
                          onFeeDelete={handleFeeDelete(i)}
                        />
                      ))}
                    {formData.get('fees') && (
                      <AddFeeSection onFeeAdd={handleFeeAdd} />
                    )}
                  </div>
                </div>
                <div>
                  {formData.get('taxBreakdown') && (
                    <div className="header">Taxes:</div>
                  )}
                  {formData.get('taxBreakdown') && (
                    <div className="indent">
                      <SummaryRow
                        name="stateTax"
                        label="State Tax %"
                        value={
                          100 * get('state.rate')(formData.get('taxBreakdown'))
                        }
                        onChange={handleTaxChange('state')}
                        precision={2}
                      />
                      <SummaryRow
                        name="countyTax"
                        label="County Tax %"
                        value={
                          100 * get('county.rate')(formData.get('taxBreakdown'))
                        }
                        onChange={handleTaxChange('county')}
                        precision={2}
                      />
                      <SummaryRow
                        name="cityTax"
                        label="City Tax %"
                        value={
                          100 * get('city.rate')(formData.get('taxBreakdown'))
                        }
                        onChange={handleTaxChange('city')}
                        precision={2}
                      />
                    </div>
                  )}
                </div>
                <div>
                  <div className="header">Pencil Configuration:</div>
                  <div>(Change in Setup &gt;&gt; Configuration)</div>
                  <br />
                  <div className="indent">
                    <div>
                      Include Tax in Cap Cost:&nbsp;
                      <Toggle
                        on="On"
                        off="Off"
                        className="toggle-switch"
                        active={get('desking.includeTaxInCapCost')(dealer)}
                        disabled
                      />
                    </div>
                    <div>
                      Add Inception Fees to Total at Start:&nbsp;
                      <Toggle
                        on="On"
                        off="Off"
                        className="toggle-switch"
                        active={get('desking.addInceptionFeesToTotalAtStart')(
                          dealer,
                        )}
                        disabled
                      />
                    </div>
                    <div>
                      Apply Rebates to Due At Signing:&nbsp;
                      <Toggle
                        on="On"
                        off="Off"
                        className="toggle-switch"
                        active={get('desking.applyRebatesToDueAtSigning')(
                          dealer,
                        )}
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </Col>
              <Col md={3} className="rightSummary">
                <div>
                  {carResidualValues && (
                    <div className="header">
                      Residual Values ({formData.get('term')} months):
                    </div>
                  )}
                  <div className="indent">
                    {carResidualValues &&
                      Object.entries(carResidualValues).map(([key, value]) => (
                        <div key={key}>
                          <div>Mileage: {key}</div>
                          <div>
                            Residual Value: {value} (
                            {toFixed(
                              (100 * value) /
                                getAppliedMRM(
                                  formData.get('mrm'),
                                  formData.get('msrp'),
                                ),
                              1,
                            )}
                            %)
                          </div>
                          <br />
                        </div>
                      ))}
                  </div>
                </div>
                <div>
                  {formData.get('programs') &&
                    !isEmpty(formData.get('programs')) && (
                      <div className="header">Programs:</div>
                    )}
                  {formData.get('programs') &&
                    formData.get('programs').map(program => (
                      <div key={program.id} className="indent">
                        <div>Description: {program.description}</div>
                        <div>Eligibility: {program.eligibility}</div>
                        <div>Start Date: {program.startDate}</div>
                        <div>End Date: {program.endDate}</div>
                        <div>Applied APR: {program.appliedApr}</div>
                        <div>Applied Cash: {program.appliedCash}</div>
                        {program.residuals && (
                          <div className="residual-percent-map">
                            Residuals:
                            {Object.entries(program.residuals).map(
                              ([term, termMap]) => (
                                <div>
                                  Term: {term} <br />
                                  {Object.entries(termMap).map(
                                    ([mileage, percent]) => (
                                      <div>
                                        Mileage: {mileage} - {percent}%
                                      </div>
                                    ),
                                  )}
                                </div>
                              ),
                            )}
                          </div>
                        )}
                        <br />
                      </div>
                    ))}
                </div>
              </Col>
            </Row>
          </Grid>
        </div>
      </div>
    </SidebarContainer>
  );
}

Worksheet.propTypes = {
  actions: PropTypes.object,
  dealer: PropTypes.object,
  car: PropTypes.object,
  deal: PropTypes.object,
  payments: PropTypes.object,
  pencil: PropTypes.object,
  error: PropTypes.string,
  match: PropTypes.object,
  fetching: PropTypes.bool,
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ ...carActions, ...dealActions }, dispatch),
});

const mapStateToProps = createSelector(
  [getDealer, getCar, getDeal, getPayments, getPencil, getError, getFetching],
  (dealer, car, deal, payments, pencil, error, fetching) => ({
    dealer,
    car,
    deal,
    payments,
    pencil,
    error,
    fetching,
  }),
);

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(Worksheet),
);
